import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  Box,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { ArrowBack, Category } from '@mui/icons-material';

import { ServiceDetailsComponent } from './components/servicedetail';
import { EditableListComponent } from './components/EditableListComponent';
import {
  createNewService,
  EditService,
  GETSingleService,
} from '../../services/service';
import { IService } from '../../utils/entities/interface';
import withComponentAuth from '../../hoc/withComponentAuth';
import useSnackbar from '../../hooks/useSnackbar';

const ServiceManagement = () => {
  const { serviceId } = useParams<{ serviceId?: string }>();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'view' | 'edit' | 'add'>('add');
  const [serviceDetails, setServiceDetails] = useState<IService | null>(null);
  const [originalServiceDetails, setOriginalServiceDetails] =
    useState<IService | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  useEffect(() => {
    if (serviceId) {
      setMode('view');
      fetchServiceDetails(serviceId);
    }
  }, [serviceId]);

  console.log('serviceDetails', serviceDetails);
  const fetchServiceDetails = async (id: string) => {
    const data = await GETSingleService(id);
    setServiceDetails(data);
    setOriginalServiceDetails(data); // Store original data
  };

  // Check if service details have changed
  useEffect(() => {
    if (serviceDetails && originalServiceDetails) {
      setIsSaveEnabled(
        JSON.stringify(serviceDetails) !==
          JSON.stringify(originalServiceDetails),
      );
    }
  }, [serviceDetails, originalServiceDetails]);

  const handleEdit = () => {
    setShowConfirmationDialog(true);
  };

  const handleConfirmationDialogClose = (confirm: boolean) => {
    if (confirm) {
      setMode('edit');
    }
    setShowConfirmationDialog(false);
  };

  console.log('serviceDetails', serviceDetails);
  const handleSave = async () => {
    if (!serviceDetails?.name || !serviceDetails?.category) {
      showSnackbar('Required Fields Missings', 'warning');
      return;
    }
    try {
      if (!serviceDetails) return null;
      const status = await EditService({
        serviceId: serviceDetails._id,
        name: serviceDetails.name,
        description: serviceDetails.description,
        terms: serviceDetails.terms,
        icon: serviceDetails.icon,
        category: serviceDetails.category,
        banner_images: serviceDetails.banner_images,
      });

      if (status == 201) {
        showSnackbar('Service Updated Successfully', 'success');
        reloadService(serviceDetails._id);
        setMode('view');
      } else {
        showSnackbar('Failed While Updating Service', 'error');
      }
    } catch (error: unknown) {
      let errorMessage = 'An unknown error occurred';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      showSnackbar(`An error occurred: ${errorMessage}`, 'error');
      console.error('Error updating service:', error);
    } finally {
      setShowConfirmationDialog(false);
    }
  };

  const reloadService = (id: string) => {
    fetchServiceDetails(id);
  };

  const handleReset = () => {
    if (mode != 'add' && serviceId) {
      setMode('view');
      reloadService(serviceId);
    }
  };

  const updateDescription = (newItems: React.SetStateAction<string[]>) => {
    setServiceDetails((prev) => {
      if (!prev) return null;
      const updatedDescription =
        typeof newItems === 'function' ? newItems(prev.description) : newItems;
      return { ...prev, description: updatedDescription };
    });
  };

  const updateTerms = (newItems: React.SetStateAction<string[]>) => {
    setServiceDetails((prev) => {
      if (!prev) return null;
      const updatedTerms =
        typeof newItems === 'function' ? newItems(prev.terms) : newItems;
      return { ...prev, terms: updatedTerms };
    });
  };

  const handleCreate = async () => {
    // setLoading(true);

    try {
      if (!serviceDetails?.name || !serviceDetails?.category) {
        showSnackbar('Required Fields Missings', 'warning');
        return;
      }
      const status = await createNewService({
        name: serviceDetails?.name,
        description: Array.isArray(serviceDetails?.description)
          ? serviceDetails?.description
          : [],
        terms: Array.isArray(serviceDetails?.terms)
          ? serviceDetails?.terms
          : [],
        category: serviceDetails?.category,
        banner_images: Array.isArray(serviceDetails.banner_images)
          ? serviceDetails.banner_images
          : [],
        icon: serviceDetails.icon,
      });

      if (status == 201) {
        showSnackbar('Service created successfully!', 'success');
        setTimeout(() => {
          navigate('/manage/services');
        }, 1000);
      } else {
        showSnackbar('Service created failed:', 'error');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create service';
      showSnackbar('Service created failed:' + errorMessage, 'error');
      console.error('Service creation failed:', errorMessage);
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Service details updated:', serviceDetails);
  }, [serviceDetails]);

  return (
    <Box
      sx={{
        p: 1,
        maxHeight: '100%',
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      {SnackbarComponent}
      <Grid container spacing={2} sx={{ maxHeight: '100%', height: '100%' }}>
        <Grid
          item
          xs={12}
          sx={{
            height: '10%',
            maxHeight: '10%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box display={'flex'} alignItems={'center'}>
            <IconButton
              sx={{ color: '#BB86FC' }}
              onClick={() => {
                navigate(-1);
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="body1"
              sx={{ marginLeft: 1, fontWeight: 'bold' }}
            >
              {mode !== 'add' ? serviceDetails?.name : 'Create Service'}
            </Typography>
          </Box>
          <Box>
            {mode === 'view' && (
              <Button variant="contained" onClick={() => setMode('edit')}>
                Edit
              </Button>
            )}
            {mode === 'edit' && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleReset}
                  sx={{ marginRight: 1 }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleEdit}
                  disabled={!isSaveEnabled}
                >
                  Save
                </Button>
              </>
            )}
            {mode == 'add' && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreate}
                // disabled={!isSaveEnabled}
              >
                Create
              </Button>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <ServiceDetailsComponent
            serviceDetails={serviceDetails}
            setServiceDetails={setServiceDetails}
            mode={mode}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            maxHeight: '90%',
            height: '90%',
          }}
        >
          <EditableListComponent
            label="Descriptions"
            items={serviceDetails?.description}
            setItems={updateDescription}
            mode={mode}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            maxHeight: '90%',
            height: '90%',
          }}
        >
          <EditableListComponent
            label="Terms and Conditions"
            items={serviceDetails?.terms}
            setItems={updateTerms}
            mode={mode}
          />
        </Grid>
      </Grid>

      {/* Confirmation Dialog for Edit */}
      <Dialog
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
      >
        <DialogTitle>Confirm Edit</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to edit this service?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleConfirmationDialogClose(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withComponentAuth(ServiceManagement);
